/**
 * Whether this browser was signed in when Orik last asked, so the next page load can start the app at once.
 *
 * The session cookie is HttpOnly, so the page cannot read it; it learns who is signed in from `/api/session`,
 * one round trip after the page arrives. Every step that boots the harness — its chunks, its kernel, the
 * Remote-stream socket, the session list and the conversation — used to wait for that answer, which a
 * far-away browser pays for in full before anything starts. The hint lets the app render optimistically in
 * parallel with the check; the check still decides, and a stale hint costs one reload into the right page.
 *
 * It is a convenience, never an authorization: every request the app makes is authenticated by the cookie.
 */
const HINT_KEY = "atlas:signed-in";

/** The user the last authenticated session answer named, or undefined. */
export function readSignedInHint(): string | undefined {
  try { return localStorage.getItem(HINT_KEY) ?? undefined; } catch { return undefined; }
}

/** Record (or, with undefined, forget) the user an authoritative session answer named. */
export function writeSignedInHint(user: string | undefined): void {
  try {
    if (user === undefined) localStorage.removeItem(HINT_KEY);
    else localStorage.setItem(HINT_KEY, user);
  } catch { /* blocked storage: no optimistic start, nothing else changes */ }
}

/** Reload into the page the session check decides; a seam, since a browser test cannot stub `location.reload`. */
export function reloadForSession(): void {
  window.location.reload();
}
